import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Switch } from '../../components/ui/Switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { Select } from '../../components/ui/Select';
import { Alert, AlertDescription } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { 
  Shield, Camera, Mic, MapPin, Link2, Eye, 
  Save, Loader2, CheckCircle2, AlertTriangle
} from 'lucide-react';
import { useSecurityConfig, useUpdateSecurityConfig } from '../../hooks/useAdvancedSecurity';
import { useAuthStore } from '../../stores/auth.store';

const operationTypes = [
  { value: 'PIX', label: 'PIX' },
  { value: 'TED', label: 'TED' },
  { value: 'TRANSFER', label: 'Transferência Interna' },
  { value: 'BILL_PAYMENT', label: 'Pagamento de Boleto' },
  { value: 'CRYPTO_BUY', label: 'Compra de Crypto' },
  { value: 'CRYPTO_SELL', label: 'Venda de Crypto' },
  { value: 'CRYPTO_WITHDRAW', label: 'Saque de Crypto' },
  { value: 'TOKEN_TRADE', label: 'Trading de Tokens' },
  { value: 'CONTRACT_SIGN', label: 'Assinatura de Contrato' },
];

const countries = [
  { code: 'BR', name: 'Brasil' },
  { code: 'US', name: 'Estados Unidos' },
  { code: 'PT', name: 'Portugal' },
  { code: 'ES', name: 'Espanha' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'MX', name: 'México' },
  { code: 'CO', name: 'Colômbia' },
];

const blockchainNetworks = [
  { value: 'polygon', label: 'Polygon' },
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'bsc', label: 'BSC' },
  { value: 'drex', label: 'DREX (Hyperledger)' },
];

const phraseComplexityOptions = [
  { value: 'LOW', label: 'Baixa' },
  { value: 'MEDIUM', label: 'Média' },
  { value: 'HIGH', label: 'Alta' },
];

export function AdvancedSecurityConfig() {
  const { user } = useAuthStore();
  const tenantId = user?.tenantId;
  
  const { data: config, isLoading } = useSecurityConfig(tenantId);
  const updateMutation = useUpdateSecurityConfig();
  
  const [localConfig, setLocalConfig] = useState(config);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (config && !localConfig) {
      setLocalConfig(config);
    }
  }, [config, localConfig]);

  const handleChange = (key: string, value: unknown) => {
    setLocalConfig(prev => prev ? { ...prev, [key]: value } : prev);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!localConfig) return;
    
    try {
      await updateMutation.mutateAsync({
        tenantId,
        config: localConfig,
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving config:', error);
    }
  };

  const toggleOperation = (key: string, operation: string) => {
    const current = (localConfig as unknown as Record<string, string[]>)?.[key] || [];
    const updated = current.includes(operation)
      ? current.filter((o: string) => o !== operation)
      : [...current, operation];
    handleChange(key, updated);
  };

  const toggleCountry = (key: string, countryCode: string) => {
    const current = (localConfig as unknown as Record<string, string[]>)?.[key] || [];
    const updated = current.includes(countryCode)
      ? current.filter((c: string) => c !== countryCode)
      : [...current, countryCode];
    handleChange(key, updated);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!config || !localConfig) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Configuração de segurança não encontrada. Verifique se o módulo está ativo.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            Segurança Avançada
          </h1>
          <p className="text-gray-500 mt-1">
            Configure as opções de verificação biométrica e segurança
          </p>
        </div>
        
        <Button 
          onClick={handleSave} 
          disabled={!hasChanges || updateMutation.isPending}
        >
          {updateMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Salvar Alterações
        </Button>
      </div>

      {updateMutation.isSuccess && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">
            Configurações salvas com sucesso!
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="biometric" className="space-y-4">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="biometric" className="flex items-center gap-1">
            <Camera className="h-4 w-4" />
            <span className="hidden sm:inline">Biometria</span>
          </TabsTrigger>
          <TabsTrigger value="liveness" className="flex items-center gap-1">
            <Eye className="h-4 w-4" />
            <span className="hidden sm:inline">Liveness</span>
          </TabsTrigger>
          <TabsTrigger value="geolocation" className="flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            <span className="hidden sm:inline">Localização</span>
          </TabsTrigger>
          <TabsTrigger value="blockchain" className="flex items-center gap-1">
            <Link2 className="h-4 w-4" />
            <span className="hidden sm:inline">Blockchain</span>
          </TabsTrigger>
          <TabsTrigger value="voice" className="flex items-center gap-1">
            <Mic className="h-4 w-4" />
            <span className="hidden sm:inline">Voz</span>
          </TabsTrigger>
        </TabsList>

        {/* Biometric Tab */}
        <TabsContent value="biometric">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Verificação Biométrica
              </CardTitle>
              <CardDescription>
                Configure verificação facial e assinatura manuscrita
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Verificação Facial</Label>
                    <p className="text-sm text-gray-500">Habilitar reconhecimento facial</p>
                  </div>
                  <Switch
                    checked={localConfig.facialVerificationEnabled}
                    onCheckedChange={(v) => handleChange('facialVerificationEnabled', v)}
                  />
                </div>

                {localConfig.facialVerificationEnabled && (
                  <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-blue-200">
                    <div>
                      <Label>Threshold Alto (%)</Label>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={localConfig.facialThresholdHigh * 100}
                        onChange={(e) => handleChange('facialThresholdHigh', Number(e.target.value) / 100)}
                      />
                    </div>
                    <div>
                      <Label>Threshold Médio (%)</Label>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={localConfig.facialThresholdMedium * 100}
                        onChange={(e) => handleChange('facialThresholdMedium', Number(e.target.value) / 100)}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Assinatura Manuscrita</Label>
                    <p className="text-sm text-gray-500">Habilitar verificação de assinatura</p>
                  </div>
                  <Switch
                    checked={localConfig.signatureVerificationEnabled}
                    onCheckedChange={(v) => handleChange('signatureVerificationEnabled', v)}
                  />
                </div>

                {localConfig.signatureVerificationEnabled && (
                  <div className="pl-4 border-l-2 border-blue-200">
                    <Label>Complexidade Mínima</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={localConfig.signatureMinComplexity}
                      onChange={(e) => handleChange('signatureMinComplexity', Number(e.target.value))}
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Verificação Combinada</Label>
                  <p className="text-sm text-gray-500">Exigir facial + assinatura juntos</p>
                </div>
                <Switch
                  checked={localConfig.combinedVerificationEnabled}
                  onCheckedChange={(v) => handleChange('combinedVerificationEnabled', v)}
                />
              </div>

              <div className="space-y-2">
                <Label>Operações que Requerem Verificação Facial</Label>
                <div className="flex flex-wrap gap-2">
                  {operationTypes.map(op => (
                    <Badge
                      key={op.value}
                      variant={localConfig.operationsRequiringFacial?.includes(op.value) ? 'primary' : 'default'}
                      className="cursor-pointer"
                      onClick={() => toggleOperation('operationsRequiringFacial', op.value)}
                    >
                      {op.label}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Liveness Tab */}
        <TabsContent value="liveness">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Prova de Vida Contínua
              </CardTitle>
              <CardDescription>
                Configure desafios de liveness durante operações
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Habilitar Liveness</Label>
                  <p className="text-sm text-gray-500">Ativar prova de vida contínua</p>
                </div>
                <Switch
                  checked={localConfig.livenessEnabled}
                  onCheckedChange={(v) => handleChange('livenessEnabled', v)}
                />
              </div>

              {localConfig.livenessEnabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Valor Mínimo (R$)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={localConfig.livenessThresholdValue}
                      onChange={(e) => handleChange('livenessThresholdValue', Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label>Intervalo (minutos)</Label>
                    <Input
                      type="number"
                      min={1}
                      value={localConfig.livenessIntervalMinutes}
                      onChange={(e) => handleChange('livenessIntervalMinutes', Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label>Máximo de Falhas</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={localConfig.livenessMaxFailures}
                      onChange={(e) => handleChange('livenessMaxFailures', Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label>Timeout do Desafio (s)</Label>
                    <Input
                      type="number"
                      min={5}
                      max={60}
                      value={localConfig.livenessChallengeTimeout}
                      onChange={(e) => handleChange('livenessChallengeTimeout', Number(e.target.value))}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Geolocation Tab */}
        <TabsContent value="geolocation">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Geolocalização
              </CardTitle>
              <CardDescription>
                Configure verificação de localização e restrições geográficas
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Localização Obrigatória</Label>
                  <p className="text-sm text-gray-500">Exigir localização em operações</p>
                </div>
                <Switch
                  checked={localConfig.geolocationRequired}
                  onCheckedChange={(v) => handleChange('geolocationRequired', v)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Distância Incomum (km)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={localConfig.unusualDistanceThreshold}
                    onChange={(e) => handleChange('unusualDistanceThreshold', Number(e.target.value))}
                  />
                </div>
                <div>
                  <Label>Velocidade Impossível (km/h)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={localConfig.impossibleTravelSpeedKmh}
                    onChange={(e) => handleChange('impossibleTravelSpeedKmh', Number(e.target.value))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Países Permitidos</Label>
                <div className="flex flex-wrap gap-2">
                  {countries.map(country => (
                    <Badge
                      key={country.code}
                      variant={localConfig.geolocationAllowedCountries?.includes(country.code) ? 'success' : 'default'}
                      className="cursor-pointer"
                      onClick={() => toggleCountry('geolocationAllowedCountries', country.code)}
                    >
                      {country.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Blockchain Tab */}
        <TabsContent value="blockchain">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="h-5 w-5" />
                Blockchain de Assinaturas
              </CardTitle>
              <CardDescription>
                Configure registro de assinaturas em blockchain
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Habilitar Blockchain</Label>
                  <p className="text-sm text-gray-500">Registrar assinaturas em blockchain</p>
                </div>
                <Switch
                  checked={localConfig.blockchainEnabled}
                  onCheckedChange={(v) => handleChange('blockchainEnabled', v)}
                />
              </div>

              {localConfig.blockchainEnabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Rede Blockchain</Label>
                    <Select
                      value={localConfig.blockchainNetwork}
                      onChange={(e) => handleChange('blockchainNetwork', e.target.value)}
                      options={blockchainNetworks}
                    />
                  </div>
                  <div>
                    <Label>Valor Mínimo (R$)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={localConfig.blockchainThresholdValue}
                      onChange={(e) => handleChange('blockchainThresholdValue', Number(e.target.value))}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Voice Tab */}
        <TabsContent value="voice">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mic className="h-5 w-5" />
                Assinatura por Voz
              </CardTitle>
              <CardDescription>
                Configure verificação por voiceprint
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Habilitar Voz</Label>
                  <p className="text-sm text-gray-500">Ativar verificação por voz</p>
                </div>
                <Switch
                  checked={localConfig.voiceEnabled}
                  onCheckedChange={(v) => handleChange('voiceEnabled', v)}
                />
              </div>

              {localConfig.voiceEnabled && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Confiança Mínima (%)</Label>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={localConfig.voiceConfidenceThreshold * 100}
                        onChange={(e) => handleChange('voiceConfidenceThreshold', Number(e.target.value) / 100)}
                      />
                    </div>
                    <div>
                      <Label>Complexidade da Frase</Label>
                      <Select
                        value={localConfig.voicePhraseComplexity}
                        onChange={(e) => handleChange('voicePhraseComplexity', e.target.value)}
                        options={phraseComplexityOptions}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Anti-Spoofing</Label>
                      <p className="text-sm text-gray-500">Detectar gravações e deepfakes</p>
                    </div>
                    <Switch
                      checked={localConfig.voiceAntiSpoofingEnabled}
                      onCheckedChange={(v) => handleChange('voiceAntiSpoofingEnabled', v)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Operações que Requerem Voz</Label>
                    <div className="flex flex-wrap gap-2">
                      {operationTypes.map(op => (
                        <Badge
                          key={op.value}
                          variant={localConfig.operationsRequiringVoice?.includes(op.value) ? 'primary' : 'default'}
                          className="cursor-pointer"
                          onClick={() => toggleOperation('operationsRequiringVoice', op.value)}
                        >
                          {op.label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default AdvancedSecurityConfig;
